// client/src/components/tendermaker/EditorToolbar.js
import React, { useState } from "react";

const tbtn = {
  padding: "5px 8px",
  borderRadius: 6,
  border: "1px solid #e5e7eb",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 12,
  minWidth: 28,
  lineHeight: 1,
};
const tbtnActive = { ...tbtn, background: "#e8f0ff", borderColor: "#93b4f5" };
const tbtnGroup = { display: "flex", gap: 2, alignItems: "center" };
const divider = {
  width: 1,
  height: 22,
  background: "#e5e7eb",
  margin: "0 6px",
  flexShrink: 0,
};

function ToolBtn({ editor, command, isActive, label, title, disabled }) {
  const active = isActive && editor?.isActive(isActive);
  return (
    <button
      style={active ? tbtnActive : tbtn}
      onClick={() => editor && command(editor.chain().focus())}
      title={title || label}
      disabled={disabled || !editor}
    >
      {label}
    </button>
  );
}

export default function EditorToolbar({
  editor,
  profileDocs,
  onInsertVaultImage,
}) {
  const [showHeadingMenu, setShowHeadingMenu] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  if (!editor) return null;

  const colors = [
    "#000000", "#374151", "#dc2626", "#ea580c", "#ca8a04",
    "#16a34a", "#2563eb", "#7c3aed", "#db2777",
  ];

  const headingLevels = [
    { level: 1, label: "Heading 1" },
    { level: 2, label: "Heading 2" },
    { level: 3, label: "Heading 3" },
    { level: 4, label: "Heading 4" },
  ];

  const currentHeading = headingLevels.find((h) => editor.isActive("heading", { level: h.level }));

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        alignItems: "center",
        padding: "6px 8px",
        background: "#f9fafb",
        borderRadius: 10,
        border: "1px solid #e5e7eb",
      }}
    >
      {/* === Text Formatting === */}
      <div style={tbtnGroup}>
        <ToolBtn
          editor={editor}
          command={(c) => c.toggleBold().run()}
          isActive="bold"
          label="B"
          title="Bold"
        />
        <ToolBtn
          editor={editor}
          command={(c) => c.toggleItalic().run()}
          isActive="italic"
          label="I"
          title="Italic"
        />
        <ToolBtn
          editor={editor}
          command={(c) => c.toggleUnderline().run()}
          isActive="underline"
          label="U"
          title="Underline"
        />
        <ToolBtn
          editor={editor}
          command={(c) => c.toggleStrike().run()}
          isActive="strike"
          label="S"
          title="Strikethrough"
        />

        {/* Color picker */}
        <div style={{ position: "relative" }}>
          <button
            style={tbtn}
            onClick={() => setShowColorPicker((v) => !v)}
            title="Text color"
          >
            A<span style={{ color: "#dc2626", fontWeight: 900 }}>.</span>
          </button>
          {showColorPicker && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                zIndex: 50,
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 6,
                display: "grid",
                gridTemplateColumns: "repeat(5, 24px)",
                gap: 4,
                marginTop: 4,
                boxShadow: "0 4px 12px rgba(0,0,0,.12)",
              }}
            >
              {colors.map((c) => (
                <button
                  key={c}
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                    border: "1px solid #d1d5db",
                    background: c,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    editor.chain().focus().setColor(c).run();
                    setShowColorPicker(false);
                  }}
                />
              ))}
              <button
                style={{
                  ...tbtn,
                  gridColumn: "1 / -1",
                  fontSize: 11,
                  padding: "3px 4px",
                }}
                onClick={() => {
                  editor.chain().focus().unsetColor().run();
                  setShowColorPicker(false);
                }}
              >
                Reset
              </button>
            </div>
          )}
        </div>

        <ToolBtn
          editor={editor}
          command={(c) => c.toggleHighlight({ color: "#fef08a" }).run()}
          isActive="highlight"
          label="Hi"
          title="Highlight"
        />
      </div>

      <div style={divider} />

      {/* === Paragraph === */}
      <div style={tbtnGroup}>
        {/* Heading dropdown */}
        <div style={{ position: "relative" }}>
          <button
            style={tbtn}
            onClick={() => setShowHeadingMenu((v) => !v)}
            title="Heading level"
          >
            {currentHeading ? currentHeading.label.replace("Heading ", "H") : "H"}
            <span style={{ fontSize: 9, marginLeft: 2 }}>&#9662;</span>
          </button>
          {showHeadingMenu && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                zIndex: 50,
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 4,
                marginTop: 4,
                boxShadow: "0 4px 12px rgba(0,0,0,.12)",
                minWidth: 130,
              }}
            >
              <button
                style={{ ...tbtn, width: "100%", textAlign: "left", marginBottom: 2 }}
                onClick={() => {
                  editor.chain().focus().setParagraph().run();
                  setShowHeadingMenu(false);
                }}
              >
                Normal text
              </button>
              {headingLevels.map((h) => (
                <button
                  key={h.level}
                  style={{
                    ...(editor.isActive("heading", { level: h.level }) ? tbtnActive : tbtn),
                    width: "100%",
                    textAlign: "left",
                    marginBottom: 2,
                    fontSize: 14 - h.level,
                    fontWeight: 900,
                  }}
                  onClick={() => {
                    editor.chain().focus().toggleHeading({ level: h.level }).run();
                    setShowHeadingMenu(false);
                  }}
                >
                  {h.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <ToolBtn
          editor={editor}
          command={(c) => c.toggleBulletList().run()}
          isActive="bulletList"
          label="&#8226;"
          title="Bullet list"
        />
        <ToolBtn
          editor={editor}
          command={(c) => c.toggleOrderedList().run()}
          isActive="orderedList"
          label="1."
          title="Numbered list"
        />

        <ToolBtn
          editor={editor}
          command={(c) => c.setTextAlign("left").run()}
          label="&#8676;"
          title="Align left"
        />
        <ToolBtn
          editor={editor}
          command={(c) => c.setTextAlign("center").run()}
          label="&#8596;"
          title="Align center"
        />
        <ToolBtn
          editor={editor}
          command={(c) => c.setTextAlign("right").run()}
          label="&#8677;"
          title="Align right"
        />
      </div>

      <div style={divider} />

      {/* === Insert === */}
      <div style={tbtnGroup}>
        <button
          style={tbtn}
          title="Insert 3x3 table"
          disabled={!editor}
          onClick={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          }
        >
          Table
        </button>
        <ToolBtn
          editor={editor}
          command={(c) => c.setHorizontalRule().run()}
          label="&mdash;"
          title="Horizontal rule"
        />
        <button
          style={tbtn}
          title="Insert image from file"
          disabled={!editor}
          onClick={() => {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/*";
            input.onchange = async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => {
                editor.chain().focus().setImage({ src: reader.result }).run();
              };
              reader.readAsDataURL(file);
            };
            input.click();
          }}
        >
          Image
        </button>
        <button
          style={tbtn}
          title="Page break (for PDF export)"
          disabled={!editor}
          onClick={() => {
            editor
              .chain()
              .focus()
              .insertContent('<div class="page-break" style="page-break-after:always;border-top:2px dashed #d1d5db;margin:16px 0;height:0;"></div><p></p>')
              .run();
          }}
        >
          Page Break
        </button>
      </div>

      {/* === Table controls (shown when inside a table) === */}
      {editor.isActive("table") && (
        <>
          <div style={divider} />
          <div style={tbtnGroup}>
            <button
              style={tbtn}
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              title="Add column"
            >
              +Col
            </button>
            <button
              style={tbtn}
              onClick={() => editor.chain().focus().addRowAfter().run()}
              title="Add row"
            >
              +Row
            </button>
            <button
              style={tbtn}
              onClick={() => editor.chain().focus().deleteColumn().run()}
              title="Delete column"
            >
              -Col
            </button>
            <button
              style={tbtn}
              onClick={() => editor.chain().focus().deleteRow().run()}
              title="Delete row"
            >
              -Row
            </button>
            <button
              style={{ ...tbtn, color: "#dc2626" }}
              onClick={() => editor.chain().focus().deleteTable().run()}
              title="Delete table"
            >
              Del Table
            </button>
          </div>
        </>
      )}
    </div>
  );
}
